import {
  createPublicTelemetryProperties,
  PUBLIC_TELEMETRY_EVENTS,
  resolvePublicTelemetrySurface,
} from './publicTelemetryContract.js';

const EVENT_ENDPOINT = '/api/telemetry/posthog';
const MAX_BATCH_SIZE = 12;
const FLUSH_INTERVAL_MS = 4000;

let telemetryState = {
  enabled: false,
  hasAnalyticsConsent: false,
  hasMarketingConsent: false,
  sessionId: null,
};

let queue = [];
let flushTimer = null;
let unloadHandlersRegistered = false;

const isBrowser = typeof window !== 'undefined';

const scheduleFlush = (immediate = false) => {
  if (!telemetryState.enabled || queue.length === 0) {
    return;
  }

  if (immediate) {
    flushQueue();
    return;
  }

  if (flushTimer) {
    return;
  }

  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushQueue();
  }, FLUSH_INTERVAL_MS);
};

const sendBatch = async (events, { useBeacon = false } = {}) => {
  if (events.length === 0) {
    return true;
  }

  try {
    const payload = JSON.stringify({ events });
    if (
      useBeacon &&
      typeof navigator !== 'undefined' &&
      typeof navigator.sendBeacon === 'function'
    ) {
      const beaconSucceeded = navigator.sendBeacon(EVENT_ENDPOINT, payload);
      return beaconSucceeded;
    }

    const response = await fetch(EVENT_ENDPOINT, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: payload,
      keepalive: useBeacon,
    });

    if (!response.ok) {
      console.warn('[telemetry] Failed to deliver events', { status: response.status });
      return false;
    }

    return true;
  } catch {
    console.warn('[telemetry] Network error while delivering events');
    return false;
  }
};

const flushQueue = async ({ forceBeacon = false } = {}) => {
  if (!telemetryState.enabled || queue.length === 0) {
    return;
  }

  const batch = queue.splice(0, MAX_BATCH_SIZE);
  const delivered = await sendBatch(batch, { useBeacon: forceBeacon });
  if (!delivered) {
    // Requeue events (prepend) if delivery failed.
    queue = batch.concat(queue);
  } else if (queue.length > 0) {
    scheduleFlush();
  }
};

const registerUnloadHandlers = () => {
  if (!isBrowser || unloadHandlersRegistered) {
    return;
  }

  unloadHandlersRegistered = true;
  const handleVisibilityChange = () => {
    if (document.visibilityState === 'hidden') {
      flushQueue({ forceBeacon: true });
    }
  };

  const handleBeforeUnload = () => {
    flushQueue({ forceBeacon: true });
  };

  window.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('beforeunload', handleBeforeUnload);
};

const buildEventPayload = (eventName, properties = {}, options = {}) => {
  const contractProperties = createPublicTelemetryProperties(eventName, properties);
  if (!contractProperties) {
    return null;
  }

  const timestamp =
    typeof options.timestamp === 'string' && options.timestamp
      ? options.timestamp
      : new Date().toISOString();

  return {
    event: eventName,
    timestamp,
    properties: contractProperties,
    sessionId: telemetryState.sessionId,
    consent: {
      analytics: telemetryState.hasAnalyticsConsent,
      marketing: telemetryState.hasMarketingConsent,
    },
  };
};

export const configureTelemetry = ({
  sessionId,
  hasAnalyticsConsent,
  hasMarketingConsent,
} = {}) => {
  const analyticsEnabled = Boolean(hasAnalyticsConsent && sessionId);
  const previousEnabled = telemetryState.enabled;

  telemetryState = {
    enabled: analyticsEnabled,
    hasAnalyticsConsent: Boolean(hasAnalyticsConsent),
    hasMarketingConsent: Boolean(hasMarketingConsent),
    sessionId: sessionId ?? null,
  };

  if (!analyticsEnabled) {
    queue = [];
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
    return;
  }

  registerUnloadHandlers();

  if (!previousEnabled && analyticsEnabled) {
    trackTelemetry(PUBLIC_TELEMETRY_EVENTS.SESSION_STARTED, {
      consent_marketing: Boolean(hasMarketingConsent),
    });
  }
};

export const trackTelemetry = (eventName, properties = {}, options = {}) => {
  if (!telemetryState.enabled) {
    return false;
  }

  const payload = buildEventPayload(eventName, properties, options);
  if (!payload) {
    return false;
  }
  queue.push(payload);

  if (queue.length >= MAX_BATCH_SIZE) {
    scheduleFlush(true);
  } else {
    scheduleFlush();
  }

  return true;
};

export const trackPublicTelemetryAction = (actionId, options = {}) => {
  try {
    const pathname = options?.pathname;
    const currentPathname = typeof pathname === 'string'
      ? pathname
      : isBrowser
        ? window.location.pathname
        : '';
    const surface = resolvePublicTelemetrySurface(currentPathname);
    if (!surface) {
      return false;
    }

    return trackTelemetry(PUBLIC_TELEMETRY_EVENTS.ACTION_SELECTED, {
      surface_id: surface.surface_id,
      action_id: actionId,
    });
  } catch {
    return false;
  }
};

export const flushTelemetry = () => flushQueue();
